import {
    FormControlLabel,
    Skeleton,
    Stack,
    Switch,
    Typography,
} from '@mui/material';

import { Order } from '@barboxx/models';

import { FaceDiagnosticsPreview } from './FaceDiagnosticsPreview';
import { FaceOverlayLegend } from './FaceOverlayLegend';

export function FaceVerificationImagesSection({
    imageUrls,
    imageUrlsLoading = false,
    diagnostics,
    showDebugOverlays,
    onShowDebugOverlaysChange,
}: {
    imageUrls?: Order.ImageUrlsResponse;
    imageUrlsLoading?: boolean;
    diagnostics?: Order.PickupFaceVerificationDiagnostics | null;
    showDebugOverlays: boolean;
    onShowDebugOverlaysChange: (checked: boolean) => void;
}) {
    if (imageUrlsLoading) {
        return (
            <Stack direction="row" gap={3} flexWrap="wrap" pt={1}>
                {['Reference Selfie', 'Pickup Selfie'].map(label => (
                    <Stack key={label} gap={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                            {label}
                        </Typography>
                        <Skeleton
                            variant="rectangular"
                            width={240}
                            height={240}
                            sx={{ borderRadius: 1 }}
                        />
                    </Stack>
                ))}
            </Stack>
        );
    }

    if (!imageUrls?.pickupSelfieUrl && !imageUrls?.referenceSelfieUrl) {
        return null;
    }

    return (
        <Stack gap={2} pt={1}>
            <FormControlLabel
                control={
                    <Switch
                        checked={showDebugOverlays}
                        onChange={event =>
                            onShowDebugOverlaysChange(event.target.checked)
                        }
                        size="small"
                    />
                }
                label="Show debug overlays"
                sx={{
                    alignSelf: 'flex-start',
                    m: 0,
                }}
            />

            <Stack direction="row" gap={3} flexWrap="wrap">
                {imageUrls.referenceSelfieUrl && (
                    <Stack gap={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                            Reference Selfie
                        </Typography>
                        <FaceDiagnosticsPreview
                            imageUrl={imageUrls.referenceSelfieUrl}
                            alt="Reference selfie"
                            faces={diagnostics?.referenceFaces || []}
                            referenceOnly
                            showDebugOverlays={showDebugOverlays}
                        />
                    </Stack>
                )}
                {imageUrls.pickupSelfieUrl && (
                    <Stack gap={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                            Pickup Selfie
                        </Typography>
                        <FaceDiagnosticsPreview
                            imageUrl={imageUrls.pickupSelfieUrl}
                            alt="Pickup selfie"
                            faces={diagnostics?.sourceFaces || []}
                            reasonCodes={diagnostics?.summary.reasonCodes}
                            decisionOutcome={
                                diagnostics?.summary.decisionOutcome
                            }
                            showDebugOverlays={showDebugOverlays}
                        />
                    </Stack>
                )}
                {showDebugOverlays ? (
                    <Stack gap={1} alignItems="flex-start" pt={0.5}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                        >
                            Legend
                        </Typography>
                        <FaceOverlayLegend />
                    </Stack>
                ) : null}
            </Stack>
        </Stack>
    );
}
